/** Google Calendar exposes a FIXED 11-entry event color palette keyed by a
 *  string `colorId`. We map inbound colorId → its hex (so the event keeps a
 *  recognizable color) and outbound custom hex → the NEAREST palette entry
 *  (lossy by nature — Google has no free-form per-event hex). */

/** colorId → hex, from Google's `colors.get` "event" palette. */
export const GOOGLE_EVENT_COLORS: Record<string, string> = {
  "1": "#7986cb", // Lavender
  "2": "#33b679", // Sage
  "3": "#8e24aa", // Grape
  "4": "#e67c73", // Flamingo
  "5": "#f6bf26", // Banana
  "6": "#f4511e", // Tangerine
  "7": "#039be5", // Peacock
  "8": "#616161", // Graphite
  "9": "#3f51b5", // Blueberry
  "10": "#0b8043", // Basil
  "11": "#d50000", // Tomato
};

/** Inbound: a Google colorId → app hex, or null when unset/unknown. */
export function colorIdToHex(colorId: string | undefined | null): string | null {
  if (!colorId) return null;
  return GOOGLE_EVENT_COLORS[colorId] ?? null;
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Outbound: an app custom hex → the nearest Google colorId (RGB distance), or
 *  undefined when there's no color or the hex can't be parsed (omit colorId). */
export function hexToColorId(hex: string | null | undefined): string | undefined {
  if (!hex) return undefined;
  const rgb = parseHex(hex);
  if (!rgb) return undefined;
  let best: string | undefined;
  let bestD = Infinity;
  for (const [id, pal] of Object.entries(GOOGLE_EVENT_COLORS)) {
    const prgb = parseHex(pal)!;
    const d =
      (rgb[0] - prgb[0]) ** 2 + (rgb[1] - prgb[1]) ** 2 + (rgb[2] - prgb[2]) ** 2;
    if (d < bestD) { bestD = d; best = id; }
  }
  return best;
}
